<script setup>
import { computed } from 'vue'

const props = defineProps({
  item: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['assign', 'claim', 'reject', 'close', 'chat', 'view-chat'])

// 状态配置（0-待处理，1-处理中，2-已完成，3-已关闭）
const statusConfig = {
  0: { label: '待处理', dot: 'bg-amber-400', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  1: { label: '处理中', dot: 'bg-blue-500', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  2: { label: '已完成', dot: 'bg-green-500', bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
  3: { label: '已关闭', dot: 'bg-slate-400', bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-200' }
}

const status = computed(() => statusConfig[props.item.status] || statusConfig[0])

// 格式化时间为相对时间
const formatRelativeTime = (dateStr) => {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  const now = new Date()
  const diff = now - date
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)

  if (minutes < 1) return '刚刚'
  if (minutes < 60) return `${minutes}分钟前`
  if (hours < 24) return `${hours}小时前`
  if (days < 30) return `${days}天前`
  return `${date.getMonth() + 1}月${date.getDate()}日`
}

// 获取用户头像首字
const getInitial = (name) => {
  if (!name) return '?'
  return name.charAt(0)
}

// 图片列表
const imageList = computed(() => {
  if (!props.item.images) return []
  return props.item.images.split(',').filter(Boolean)
})
</script>

<template>
  <div class="bg-white rounded-xl border border-slate-200 hover:shadow-md hover:border-slate-300 transition-all duration-200 overflow-hidden group">
    <!-- 顶栏：状态 + 处理人 -->
    <div class="px-5 pt-4 pb-3 flex items-center justify-between">
      <div class="flex items-center gap-3">
        <!-- 状态指示 -->
        <div class="flex items-center gap-1.5">
          <span :class="['w-2 h-2 rounded-full', status.dot]"></span>
          <span :class="['text-xs font-medium', status.text]">{{ status.label }}</span>
        </div>
      </div>
      <!-- 处理人 -->
      <div v-if="item.handlerId" class="flex items-center gap-1.5 text-xs text-slate-500">
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
        </svg>
        <span>处理人ID: {{ item.handlerId }}</span>
      </div>
    </div>

    <!-- 联系人信息行 -->
    <div class="px-5 pb-3 flex items-center justify-between">
      <div class="flex items-center gap-2.5">
        <!-- 头像 -->
        <div class="w-8 h-8 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center text-sm font-medium shrink-0">
          {{ getInitial(item.contactName) }}
        </div>
        <div class="flex items-center gap-2 text-sm">
          <span class="font-medium text-slate-800">{{ item.contactName || '未知用户' }}</span>
          <span v-if="item.contactPhone" class="text-slate-400">{{ item.contactPhone }}</span>
        </div>
      </div>
      <span class="text-xs text-slate-400 shrink-0">{{ formatRelativeTime(item.createTime) }}</span>
    </div>

    <!-- 需求描述 -->
    <div class="px-5 pb-3">
      <p class="text-sm text-slate-600 leading-relaxed line-clamp-2">{{ item.description || '暂无描述' }}</p>
    </div>

    <!-- 图片凭证 -->
    <div v-if="imageList.length > 0" class="px-5 pb-3 flex gap-2 flex-wrap">
      <img
        v-for="(img, idx) in imageList"
        :key="idx"
        :src="img"
        class="w-16 h-16 rounded-lg object-cover border border-slate-200"
        alt="凭证图片"
      />
    </div>

    <!-- 处理人回复 -->
    <div v-if="item.handlerReply" class="px-5 pb-3">
      <div class="p-3 bg-blue-50 rounded-lg">
        <p class="text-xs text-blue-600 font-medium mb-1">处理人回复</p>
        <p class="text-sm text-blue-800">{{ item.handlerReply }}</p>
      </div>
    </div>

    <!-- 操作栏 -->
    <div class="px-5 py-3 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-2">
      <!-- 待处理状态 -->
      <template v-if="item.status === 0">
        <button
          v-permission="'app:demand:assign'"
          @click="emit('assign', item)"
          class="px-3 py-1.5 text-xs font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors cursor-pointer"
        >
          分配给
        </button>
        <button
          v-permission="'app:demand:edit'"
          @click="emit('claim', item)"
          class="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors cursor-pointer"
        >
          领取
        </button>
        <button
          v-permission="'app:demand:edit'"
          @click="emit('reject', item)"
          class="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors cursor-pointer"
        >
          拒绝
        </button>
      </template>

      <!-- 处理中状态 -->
      <template v-if="item.status === 1">
        <button
          @click="emit('chat', item)"
          class="px-3 py-1.5 text-xs font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors cursor-pointer flex items-center gap-1"
        >
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
          </svg>
          发起聊天
        </button>
        <button
          v-permission="'app:demand:edit'"
          @click="emit('close', item)"
          class="px-3 py-1.5 text-xs font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors cursor-pointer"
        >
          关闭
        </button>
      </template>

      <!-- 已完成/已关闭状态 -->
      <template v-if="item.status === 2 || item.status === 3">
        <span class="text-xs text-slate-400">
          {{ item.handleTime ? `处理于 ${formatRelativeTime(item.handleTime)}` : '' }}
        </span>
      </template>
    </div>
  </div>
</template>
